import {
    ONBOARDING_AGENT_PANEL_EXPANDED_HEIGHT_OFFSET,
    ONBOARDING_AGENT_PANEL_HEIGHT,
    ONBOARDING_AGENT_PANEL_WIDTH,
    ONBOARDING_AGENT_TRIGGER_SIZE,
    ONBOARDING_AGENT_VIEWPORT_MARGIN,
} from "../constants";
import type {
    OnboardingAgentElementSize,
    OnboardingAgentPlacement,
    OnboardingAgentViewportPosition,
} from "../OnboardingAgent.types";

export type OnboardingAgentViewportSize = {
    readonly width: number;
    readonly height: number;
};

const NO_OFFSET: OnboardingAgentViewportPosition = { x: 0, y: 0 };

export const getViewportSize = (): OnboardingAgentViewportSize => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

export const getCompactPanelHeight = (): number => ONBOARDING_AGENT_PANEL_HEIGHT;

export const getExpandedPanelHeight = (): number =>
    Math.max(ONBOARDING_AGENT_PANEL_HEIGHT, getViewportSize().height - ONBOARDING_AGENT_PANEL_EXPANDED_HEIGHT_OFFSET);

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export const clampPositionToViewport = (
    position: OnboardingAgentViewportPosition,
    width: number,
    height: number,
): OnboardingAgentViewportPosition => {
    const viewport = getViewportSize();
    const maxX = Math.max(ONBOARDING_AGENT_VIEWPORT_MARGIN, viewport.width - width - ONBOARDING_AGENT_VIEWPORT_MARGIN);
    const maxY = Math.max(
        ONBOARDING_AGENT_VIEWPORT_MARGIN,
        viewport.height - height - ONBOARDING_AGENT_VIEWPORT_MARGIN,
    );

    return {
        x: clamp(position.x, ONBOARDING_AGENT_VIEWPORT_MARGIN, maxX),
        y: clamp(position.y, ONBOARDING_AGENT_VIEWPORT_MARGIN, maxY),
    };
};

/**
 * Raw coordinates of an element anchored to the trigger on the given side.
 *
 * `offset` pushes the element away from the trigger: with `{ x: 0, y: 0 }` the element's edges line
 * up with the trigger's own edges (it overlaps the trigger, which is how the panel behaves), while a
 * positive offset places the element outside the trigger with that much space in between.
 */
const createAnchoredCoordinates = (
    triggerPosition: OnboardingAgentViewportPosition,
    size: OnboardingAgentElementSize,
    placement: OnboardingAgentPlacement,
    offset: OnboardingAgentViewportPosition,
): OnboardingAgentViewportPosition => ({
    x:
        placement.horizontal === "right"
            ? triggerPosition.x + ONBOARDING_AGENT_TRIGGER_SIZE - size.width - offset.x
            : triggerPosition.x + offset.x,
    y:
        placement.vertical === "above"
            ? triggerPosition.y + ONBOARDING_AGENT_TRIGGER_SIZE - size.height - offset.y
            : triggerPosition.y + offset.y,
});

const fitsInViewport = (coordinate: number, elementSize: number, viewportSize: number): boolean =>
    coordinate >= ONBOARDING_AGENT_VIEWPORT_MARGIN &&
    coordinate + elementSize <= viewportSize - ONBOARDING_AGENT_VIEWPORT_MARGIN;

/**
 * Picks the side the element opens to: above the trigger and aligned to its right edge by default,
 * flipped only when the preferred side does not fit but the opposite one does.
 */
export const resolveAnchoredPlacement = (
    triggerPosition: OnboardingAgentViewportPosition,
    size: OnboardingAgentElementSize,
    offset: OnboardingAgentViewportPosition = NO_OFFSET,
): OnboardingAgentPlacement => {
    const viewport = getViewportSize();
    const fitsOnSide = (placement: OnboardingAgentPlacement): { horizontal: boolean; vertical: boolean } => {
        const coordinates = createAnchoredCoordinates(triggerPosition, size, placement, offset);

        return {
            horizontal: fitsInViewport(coordinates.x, size.width, viewport.width),
            vertical: fitsInViewport(coordinates.y, size.height, viewport.height),
        };
    };

    const preferredFit = fitsOnSide({ vertical: "above", horizontal: "right" });
    const flippedFit = fitsOnSide({ vertical: "below", horizontal: "left" });

    return {
        vertical: !preferredFit.vertical && flippedFit.vertical ? "below" : "above",
        horizontal: !preferredFit.horizontal && flippedFit.horizontal ? "left" : "right",
    };
};

export const resolveAnchoredPosition = (
    triggerPosition: OnboardingAgentViewportPosition,
    size: OnboardingAgentElementSize,
    placement: OnboardingAgentPlacement,
    offset: OnboardingAgentViewportPosition = NO_OFFSET,
): OnboardingAgentViewportPosition =>
    clampPositionToViewport(
        createAnchoredCoordinates(triggerPosition, size, placement, offset),
        size.width,
        size.height,
    );

export type OnboardingAgentPanelPlacement = OnboardingAgentViewportPosition & {
    readonly placement: OnboardingAgentPlacement;
};

export const resolvePanelPlacement = (
    triggerPosition: OnboardingAgentViewportPosition,
    panelHeight: number = ONBOARDING_AGENT_PANEL_HEIGHT,
): OnboardingAgentPanelPlacement => {
    const size = { width: ONBOARDING_AGENT_PANEL_WIDTH, height: panelHeight };
    const placement = resolveAnchoredPlacement(triggerPosition, size);

    return { ...resolveAnchoredPosition(triggerPosition, size, placement), placement };
};

/** Positive Y moves the panel down. Above the trigger that is toward it; below, the sign flips. */
export const getPanelSlideOffsetY = (vertical: OnboardingAgentPlacement["vertical"], distance: number): number =>
    vertical === "above" ? distance : -distance;
